import thienCan from '../data/thien_can.json';
import diaChi from '../data/dia_chi.json';
import { getCanChiYear } from './canchi_year.js';
import { getCanChiMonth } from './canchi_month.js';
import { getCanChiDay } from './canchi_day.js';
import { getCanChiHour } from './canchi_hour.js';
import {
  FIVE_ELEMENT_METHOD,
  MONTH_BRANCH_BONUS,
  TANG_CAN_WEIGHTS,
  countFiveElements,
  getCanElement
} from './five_elements.js';
import { getTenGod } from './ten_gods.js';
import {
  checkLucHop,
  checkTuHai,
  checkTuHinh,
  checkTuPha,
  checkTuXung,
  findTamHopGroups,
  findTamHinhGroups
} from './interactions.js';
import { findDungThan, getSeasonElement, resolveBodyStrength } from '../rules/dung_than.js';
import { healthRule } from '../rules/health_rules.js';
import { careerRule, CAREER_GUIDANCE } from '../rules/career_rules.js';
import { wealthRule } from '../rules/wealth_rules.js';
import { getPersonalityProfile } from '../rules/personality_rules.js';
import { RULE_IDS, getRuleMeta } from '../rules/rule_ids.js';
import { buildTieuVan } from '../fortune/tieu_van.js';
import { annualFortune } from '../fortune/annual_fortune.js';
import { getSolarTermDate } from './solar_terms.js';
import { findStars } from '../stars/shen_sha.js';
import { validateBaziInput } from '../validation/input_validator.js';
import { buildDaiVanDatasetV2 } from '../fortune/dai_van_dataset.js';
import {
  DEFAULT_TZ_OFFSET_MINUTES,
  formatLocalDateTime,
  getDateParts,
  normalizeTimezoneOffset,
  toUtcFromLocalTime
} from './time.js';

const PILLAR_KEYS = ['year', 'month', 'day', 'hour'];
const CAN_VI = {
  Giap: 'Giáp',
  At: 'Ất',
  Binh: 'Bính',
  Dinh: 'Đinh',
  Mau: 'Mậu',
  Ky: 'Kỷ',
  Canh: 'Canh',
  Tan: 'Tân',
  Nham: 'Nhâm',
  Quy: 'Quý'
};
const CHI_VI = {
  Ty: 'Tý',
  Suu: 'Sửu',
  Dan: 'Dần',
  Mao: 'Mão',
  Thin: 'Thìn',
  Ty_: 'Tỵ',
  Ngo: 'Ngọ',
  Mui: 'Mùi',
  Than: 'Thân',
  Dau: 'Dậu',
  Tuat: 'Tuất',
  Hoi: 'Hợi'
};
const ELEMENT_VI = {
  Moc: 'Mộc',
  Hoa: 'Hỏa',
  Tho: 'Thổ',
  Kim: 'Kim',
  Thuy: 'Thủy'
};
const ELEMENT_EN = {
  Moc: 'Wood',
  Hoa: 'Fire',
  Tho: 'Earth',
  Kim: 'Metal',
  Thuy: 'Water'
};
const getCanInfo = (can) => thienCan[can] || null;
const getChiInfo = (chi) => diaChi[chi] || null;
const formatDateParts = ({ year, month, day }) => {
  const pad = (value) => `${value}`.padStart(2, '0');
  return `${year}-${pad(month + 1)}-${pad(day)}`;
};

const formatHourMinute = (hourValue) => {
  const safeHour = Number.isFinite(hourValue) ? hourValue : 0;
  const hours = Math.floor(safeHour);
  const minutes = Math.round((safeHour - hours) * 60);
  const pad = (value) => `${value}`.padStart(2, '0');
  return `${pad(hours)}:${pad(minutes)}`;
};

const buildPillar = (label, can, chi, extras = {}) => {
  const canInfo = getCanInfo(can);
  const chiInfo = getChiInfo(chi);

  return {
    label: `${label}:${can} ${chi}`,
    can,
    chi,
    can_element: canInfo?.hanh || null,
    can_yin_yang: canInfo?.yin_yang || null,
    chi_element: chiInfo?.hanh || null,
    chi_tang_can: chiInfo?.tang_can || [],
    ...extras
  };
};

const buildTenGods = ({ dayMasterElement, dayMasterPolarity, pillars }) => {
  const result = {};

  PILLAR_KEYS.forEach((key) => {
    if (key === 'day') {
      result[key] = 'NhatChu';
      return;
    }
    const pillar = pillars[key];
    const targetElement = pillar?.can_element || getCanElement(pillar?.can);
    const targetPolarity = getCanInfo(pillar?.can)?.yin_yang || null;
    const samePolarity = dayMasterPolarity && targetPolarity
      ? dayMasterPolarity === targetPolarity
      : true;

    result[key] = getTenGod(dayMasterElement, targetElement, samePolarity);
  });

  return result;
};

const buildTenGodCounts = (tenGods) => {
  const counts = {};
  Object.values(tenGods).forEach((value) => {
    if (!value || value === 'NhatChu') return;
    counts[value] = (counts[value] || 0) + 1;
  });
  return counts;
};

const buildInteractions = (pillars) => {
  const branchEntries = PILLAR_KEYS.map((key) => ({ key, chi: pillars[key].chi }));
  const interactions = [];

  for (let i = 0; i < branchEntries.length; i += 1) {
    for (let j = i + 1; j < branchEntries.length; j += 1) {
      const a = branchEntries[i];
      const b = branchEntries[j];

      if (checkLucHop(a.chi, b.chi)) interactions.push({ type: 'luc_hop', pair: [a.key, b.key] });
      if (checkTuXung(a.chi, b.chi)) interactions.push({ type: 'xung', pair: [a.key, b.key] });
      if (checkTuHinh(a.chi, b.chi)) interactions.push({ type: 'hinh', pair: [a.key, b.key] });
      if (checkTuHai(a.chi, b.chi)) interactions.push({ type: 'hai', pair: [a.key, b.key] });
      if (checkTuPha(a.chi, b.chi)) interactions.push({ type: 'pha', pair: [a.key, b.key] });
    }
  }

  const branchValues = branchEntries.map((entry) => entry.chi);
  findTamHopGroups(branchValues).forEach((group) => {
    interactions.push({ type: 'tam_hop', group });
  });

  findTamHinhGroups(branchValues).forEach((group) => {
    interactions.push({ type: 'hinh_full', group });
  });

  return interactions;
};

const buildQuanHe = (interactions) => {
  const hop = [];
  const xung = [];
  const hinh = [];
  const hai = [];
  const pha = [];

  interactions.forEach((item) => {
    if (item.type === 'luc_hop') hop.push(`${item.pair.join('-')} Luc Hop`);
    if (item.type === 'tam_hop') hop.push(`${item.group.join('-')} Tam Hop`);
    if (item.type === 'hinh_full') hinh.push(`${item.group.join('-')} Tam Hinh`);
    if (item.type === 'xung') xung.push(item.pair.join('-'));
    if (item.type === 'hinh') hinh.push(item.pair.join('-'));
    if (item.type === 'hai') hai.push(item.pair.join('-'));
    if (item.type === 'pha') pha.push(item.pair.join('-'));
  });

  return { hop, xung, hinh, hai, pha };
};

const buildNguHanhSummary = (fiveElements, meta = {}) => {
  const entries = Object.entries(fiveElements);
  const total = entries.reduce((sum, [, value]) => sum + value, 0) || 1;
  const ratios = Object.fromEntries(
    entries.map(([key, value]) => [key, Math.round((value / total) * 100)])
  );

  const missing = entries.filter(([, value]) => value === 0).map(([key]) => key);
  const nhanXet = missing.length
    ? `Thiếu ${missing.join(', ')}`
    : 'Ngũ hành tương đối cân bằng';

  return {
    counts: fiveElements,
    ratios,
    nhanXet,
    method: meta.method || FIVE_ELEMENT_METHOD,
    details: meta.details || null
  };
};

const normalizeGender = (gender) => {
  if (!gender) return null;
  const normalized = gender.toString().toLowerCase();
  if (['nam', 'male', 'm'].includes(normalized)) return 'male';
  if (['nu', 'female', 'f'].includes(normalized)) return 'female';
  return null;
};

const resolveYearByLapXuan = (birthDateParts, birthHour, timezoneOffsetMinutes) => {
  let effectiveYear = birthDateParts.year;
  const lapXuanDate = getSolarTermDate(effectiveYear, 'Lap Xuan');

  if (!lapXuanDate) return effectiveYear;

  const birthUtc = toUtcFromLocalTime(birthDateParts, birthHour, timezoneOffsetMinutes);

  if (birthUtc < lapXuanDate.getTime()) {
    effectiveYear -= 1;
  }

  return effectiveYear;
};

const toElementVi = (element) => (element ? ELEMENT_VI[element] || element : null);
const toElementEn = (element) => (element ? ELEMENT_EN[element] || element : null);
const toCanVi = (can) => (can ? CAN_VI[can] || can : null);
const toChiVi = (chi) => (chi ? CHI_VI[chi] || chi : null);
const YEAR_PILLAR_ASSUMPTION = 'Năm Bát Tự đổi theo tiết Lập Xuân (solar term boundary).';

const buildRuleAttachment = (ruleId, inputs = null, outputs = null) => {
  const meta = getRuleMeta(ruleId);
  return {
    ruleId: meta.ruleId,
    ruleVersion: meta.ruleVersion,
    ruleTitle_vi: meta.ruleTitle_vi,
    scope: meta.scope,
    outputPaths: meta.outputPaths,
    inputs,
    outputs,
    explain_vi: meta.explain_vi
  };
};

const buildStandardPillar = (pillar, source, meta = null) => {
  const elements = [toElementVi(pillar.can_element), toElementVi(pillar.chi_element)].filter(Boolean);
  const base = {
    can: toCanVi(pillar.can),
    chi: toChiVi(pillar.chi),
    elements,
    source
  };
  if (meta) {
    return {
      ...base,
      meta
    };
  }
  return base;
};


const mapElementLevels = (fiveElements) => {
  const total = Object.values(fiveElements || {}).reduce((sum, value) => sum + value, 0) || 1;
  const levelForRatio = (ratio) => {
    if (ratio <= 0.15) return 'low';
    if (ratio >= 0.35) return 'high';
    return 'medium';
  };

  return {
    Wood: levelForRatio((fiveElements?.Moc || 0) / total),
    Fire: levelForRatio((fiveElements?.Hoa || 0) / total),
    Earth: levelForRatio((fiveElements?.Tho || 0) / total),
    Metal: levelForRatio((fiveElements?.Kim || 0) / total),
    Water: levelForRatio((fiveElements?.Thuy || 0) / total)
  };
};

const resolveDayMasterStrength = ({ dayMasterElement, monthBranch }) => (
  resolveBodyStrength(dayMasterElement, monthBranch)
);

/**
 * Normalize timezone offset to minutes.
 * @param {string|number|undefined|null} timezone
 * @returns {number}
 */
const parseTimezoneOffset = (timezone) => {
  if (Number.isFinite(timezone)) return timezone;
  if (!timezone) return DEFAULT_TZ_OFFSET_MINUTES;
  const normalized = timezone.toString().trim();
  if (normalized === 'Z' || normalized === '+00:00' || normalized === '+0000') return 0;
  const match = normalized.match(/^([+-])(\d{2}):?(\d{2})$/);
  if (!match) {
    throw new Error(
      `Invalid timezone format: "${normalized}". Expected "+HH:MM", "-HH:MM", or numeric offset in minutes.`
    );
  }
  const sign = match[1] === '-' ? -1 : 1;
  const hours = Number(match[2]);
  const minutes = Number(match[3]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes) || minutes >= 60) {
    throw new Error(
      `Invalid timezone components: hours="${match[2]}", minutes="${match[3]}".`
    );
  }
  return sign * (hours * 60 + minutes);
};

const parseBirthTime = (time) => {
  if (!time) throw new Error('birth.time bat buoc');
  const match = time.toString().trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) throw new Error('time khong hop le');
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (!Number.isInteger(hour) || !Number.isInteger(minute) || hour > 23 || minute > 59) {
    throw new Error('time khong hop le');
  }
  return { hour, minute };
};

const buildDateFromParts = (dateString) => {
  if (!dateString) throw new Error('birth.date bat buoc');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateString)) throw new Error('birth.date khong hop le');
  const [year, month, day] = dateString.split('-').map((value) => Number(value));
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
    throw new Error('birth.date khong hop le');
  }
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year
    || date.getUTCMonth() !== month - 1
    || date.getUTCDate() !== day
  ) {
    throw new Error('birth.date khong hop le');
  }
  return date;
};

const normalizeStandardInput = (input = {}) => {
  const { birth, calendar, gender, rolloverAt23 = false } = input;
  if (calendar && calendar !== 'gregorian') {
    throw new Error('Chi ho tro lich duong');
  }
  const birthDate = buildDateFromParts(birth?.date);
  const { hour, minute } = parseBirthTime(birth?.time);
  const birthHour = hour + minute / 60;
  const timezoneProvided = birth?.timezone !== undefined && birth?.timezone !== null && birth?.timezone !== '';
  const timezoneOffsetMinutes = parseTimezoneOffset(birth?.timezone);
  return {
    birthDate,
    birthHour,
    gender,
    timezoneOffsetMinutes,
    timezoneProvided,
    rolloverAt23: Boolean(rolloverAt23)
  };
};

const calculateBaziCore = ({
  birthDate,
  birthHour,
  gender,
  currentDate = new Date(),
  currentYear,
  timezoneOffsetMinutes = DEFAULT_TZ_OFFSET_MINUTES,
  timezoneProvided = false,
  rolloverAt23 = false
} = {}) => {
  validateBaziInput({
    birthDate,
    birthHour,
    gender,
    timezoneOffsetMinutes
  });

  const normalizedTimezoneOffset = normalizeTimezoneOffset(timezoneOffsetMinutes);
  const currentDateObj = currentDate instanceof Date ? currentDate : new Date();
  const currentLocal = new Date(currentDateObj.getTime() + normalizedTimezoneOffset * 60 * 1000);
  const currentDateParts = getDateParts(currentLocal);
  const currentLocalHour = currentLocal.getUTCHours() + currentLocal.getUTCMinutes() / 60;
  const effectiveCurrentYear = resolveYearByLapXuan(
    currentDateParts,
    currentLocalHour,
    normalizedTimezoneOffset
  );
  const fortuneYear = Number.isFinite(currentYear) ? currentYear : effectiveCurrentYear;
  const normalizedBirthHour = Number.isFinite(birthHour) ? birthHour : 0;
  const birthDateParts = getDateParts(birthDate);
  const birthYear = resolveYearByLapXuan(birthDateParts, normalizedBirthHour, normalizedTimezoneOffset);
  const yearCC = getCanChiYear(birthYear);
  const monthCC = getCanChiMonth(yearCC.canIndex, birthDateParts, normalizedBirthHour, normalizedTimezoneOffset);
  const dayCC = getCanChiDay(birthDateParts, {
    hour: normalizedBirthHour,
    timezoneOffsetMinutes: normalizedTimezoneOffset,
    rolloverAt23
  });
  const dayPillarMeta = {
    civilDate: dayCC.civilDate,
    localDate: dayCC.date,
    utcDate: dayCC.utcDate,
    timezoneOffsetMinutes: normalizedTimezoneOffset,
    birthHour: normalizedBirthHour,
    rolloverApplied: dayCC.rolloverApplied,
    rolloverAt23: dayCC.rolloverAt23,
    jdn: dayCC.jdn,
    jdnConvention: dayCC.jdnConvention,
    reference: dayCC.reference
  };
  const hourCC = getCanChiHour(dayCC.canIndex, normalizedBirthHour, {
    timezoneOffsetMinutes: normalizedTimezoneOffset
  });

  const pillars = {
    year: buildPillar('year', yearCC.can, yearCC.chi),
    month: buildPillar('month', monthCC.can, monthCC.chi, {
      solar_term: monthCC.solarTerm,
      solar_term_start: monthCC.solarTermStart,
      next_solar_term: monthCC.nextSolarTerm,
      next_solar_term_start: monthCC.nextSolarTermStart,
      month_index: monthCC.monthIndex,
      month_branch_index: monthCC.monthBranchIndex,
      month_order_from_lichun: monthCC.monthOrderFromLichun
    }),
    day: buildPillar('day', dayCC.can, dayCC.chi, {
      jdn: dayCC.jdn,
      jdn_convention: dayCC.jdnConvention,
      jdn_reference: dayCC.reference,
      meta: dayPillarMeta
    }),
    hour: buildPillar('hour', hourCC.can, hourCC.chi)
  };

  const dayMasterElement = pillars.day.can_element || getCanElement(pillars.day.can);
  const dayMasterPolarity = getCanInfo(pillars.day.can)?.yin_yang || null;

  const fiveElements = countFiveElements(Object.values(pillars), {
    monthBranch: pillars.month.chi
  });
  const fiveElementsMeta = {
    method: FIVE_ELEMENT_METHOD,
    details: {
      base: 'can_chi',
      includeTangCan: true,
      tangCanWeights: TANG_CAN_WEIGHTS,
      includeMonthBranchBonus: true,
      monthBranchBonus: MONTH_BRANCH_BONUS
    }
  };

  const tenGods = buildTenGods({
    dayMasterElement,
    dayMasterPolarity,
    pillars
  });
  const tenGodCounts = buildTenGodCounts(tenGods);

  const dungThan = findDungThan(fiveElements, {
    dayMasterElement,
    monthBranch: pillars.month.chi,
    tenGodCounts
  });

  const interactions = buildInteractions(pillars);
  const quanHe = buildQuanHe(interactions);
  const stars = findStars(pillars);

  const yearCanChi = getCanChiYear(fortuneYear);
  const normalizedGender = normalizeGender(gender);
  const birthLocalIso = formatLocalDateTime(
    new Date(toUtcFromLocalTime(birthDateParts, normalizedBirthHour, normalizedTimezoneOffset)),
    normalizedTimezoneOffset
  );
  const solarTermPrev = monthCC?.solarTerm
    ? { name: monthCC.solarTerm, at_local: formatLocalDateTime(monthCC.solarTermStart, normalizedTimezoneOffset) }
    : null;
  const solarTermNext = monthCC?.nextSolarTerm
    ? { name: monthCC.nextSolarTerm, at_local: formatLocalDateTime(monthCC.nextSolarTermStart, normalizedTimezoneOffset) }
    : null;
  const assumptionTimezoneDefault = !timezoneProvided && normalizedTimezoneOffset === DEFAULT_TZ_OFFSET_MINUTES;
  const assumptions = [YEAR_PILLAR_ASSUMPTION];
  if (assumptionTimezoneDefault) {
    assumptions.push('Không cung cấp timezone => mặc định UTC+07:00 (420 phút).');
  }
  if (rolloverAt23) {
    assumptions.push('rolloverAt23=true: Giờ Tý 23:00-23:59 tính sang ngày mới.');
  }
  let daiVanMeta = null;
  let daiVanDataset = null;
  let daiVanError = null;

  if (!normalizedGender) {
    daiVanError = 'missing_gender';
  } else {
    try {
      const result = buildDaiVanDatasetV2({
        tzOffsetMinutes: normalizedTimezoneOffset,
        birthYear: birthDateParts.year,
        birthAtLocal: birthLocalIso,
        birthDateSolar: formatDateParts(birthDateParts),
        birthTimeLocal: formatHourMinute(normalizedBirthHour),
        gender: normalizedGender,
        yearCanIndex: yearCC.canIndex,
        yearChiIndex: yearCC.chiIndex,
        monthCanIndex: monthCC.canIndex,
        monthChiIndex: monthCC.chiIndex,
        dayCanIndex: dayCC.canIndex,
        dayChiIndex: dayCC.chiIndex,
        hourCanIndex: hourCC.canIndex,
        hourChiIndex: hourCC.chiIndex,
        prevTerm: solarTermPrev,
        nextTerm: solarTermNext,
        count: 8
      });
      daiVanMeta = result.meta;
      daiVanDataset = result.dataset;
    } catch (err) {
      daiVanError = err instanceof Error ? err.message : 'dai_van_unknown_error';
    }
  }

  if (daiVanError === 'missing_gender') {
    assumptions.push('Thiếu giới tính => không tính Đại Vận; yêu cầu gender=nam/nu.');
  }

  const daiVan = { periods: daiVanDataset?.periods || [] };
  const fortune = {
    dai_van: daiVan,
    dai_van_dataset: daiVanDataset,
    dai_van_meta: daiVanMeta || (daiVanError ? { error: daiVanError } : null),
    tieu_van: buildTieuVan({ startYear: fortuneYear, count: 12 }),
    annual: annualFortune(yearCanChi.can, yearCanChi.chi, Object.values(pillars))
  };
  const healthReading = healthRule({
    fiveElements,
    tenGodCounts,
    interactionCounts: {
      hinh: quanHe.hinh.length,
      hai: quanHe.hai.length
    }
  });
  const careerReading = careerRule({
    tenGodCounts,
    fiveElements,
    interactions,
    dungThan
  });
  const wealthReading = wealthRule({
    tenGodCounts
  });

  const monthVerified = Boolean(monthCC?.solarTerm);
  return {
    pillars,
    dayMasterElement,
    dayMasterPolarity,
    fiveElements,
    fiveElementsMeta,
    tenGods,
    tenGodCounts,
    dungThan,
    interactions,
    quanHe,
    stars,
    fortune,
    daiVan,
    daiVanMeta,
    daiVanDataset,
    readings: {
      health: healthReading,
      career: careerReading,
      wealth: wealthReading
    },
    birthYearCanChi: yearCC,
    yearCanChi,
    monthCC,
    dayCC,
    monthVerified,
    normalizedBirthHour,
    normalizedTimezoneOffset,
    assumptions,
    rolloverAt23
  };
};

export function buildBaziReport(options = {}) {
  const timezoneProvided = options?.timezoneOffsetMinutes !== undefined
    && options?.timezoneOffsetMinutes !== null
    && options?.timezoneOffsetMinutes !== '';
  const timezoneOffsetMinutes = parseTimezoneOffset(options?.timezoneOffsetMinutes);
  const core = calculateBaziCore({ ...options, timezoneOffsetMinutes, timezoneProvided });
  const { pillars, dayMasterElement, dayMasterPolarity } = core;
  const engineWarnings = [];
  const seasonElement = getSeasonElement(pillars.month.chi);
  const strength = resolveDayMasterStrength({
    dayMasterElement,
    monthBranch: pillars.month.chi
  });
  if (!core.monthVerified) {
    engineWarnings.push('month_pillar_unverified');
  }
  if (!core.dungThan?.verified) {
    engineWarnings.push('dung_than_unverified');
  }

  const pillarRules = {
    year: buildRuleAttachment(
      RULE_IDS.YEAR_PILLAR_LAP_XUAN,
      { effectiveYear: core.birthYearCanChi.year, lapXuanBoundary: true },
      { can: pillars.year.can, chi: pillars.year.chi }
    ),
    month: buildRuleAttachment(
      RULE_IDS.MONTH_PILLAR_SOLAR_TERM,
      {
        solarTerm: core.monthCC?.solarTerm,
        solarTermStart: core.monthCC?.solarTermStart,
        timezoneOffsetMinutes: core.normalizedTimezoneOffset
      },
      {
        can: pillars.month.can,
        chi: pillars.month.chi,
        monthOrderFromLichun: core.monthCC?.monthOrderFromLichun || null
      }
    ),
    day: buildRuleAttachment(
      RULE_IDS.DAY_PILLAR_JDN_ROLLOVER,
      {
        jdn: core.dayCC?.jdn,
        jdnConvention: core.dayCC?.jdnConvention,
        timezoneOffsetMinutes: core.normalizedTimezoneOffset,
        rolloverAt23: core.rolloverAt23,
        rolloverApplied: core.dayCC?.rolloverApplied
      },
      { can: pillars.day.can, chi: pillars.day.chi }
    ),
    hour: buildRuleAttachment(
      RULE_IDS.HOUR_PILLAR_ZI_BOUNDARY,
      { localHour: core.normalizedBirthHour, timezoneOffsetMinutes: core.normalizedTimezoneOffset },
      { can: pillars.hour.can, chi: pillars.hour.chi }
    )
  };

  const dayMasterRule = buildRuleAttachment(
    RULE_IDS.DAY_MASTER_STRENGTH,
    {
      dayMasterElement,
      monthBranch: pillars.month.chi,
      seasonElement
    },
    {
      strength,
      constitutionKey: strength,
      relativeStrength: null,
      element: toElementEn(dayMasterElement),
      polarity: dayMasterPolarity
    }
  );
  const seasonRule = buildRuleAttachment(
    RULE_IDS.MONTH_PILLAR_SOLAR_TERM,
    {
      branch: pillars.month.chi,
      solarTerm: core.monthCC?.solarTerm,
      timezoneOffsetMinutes: core.normalizedTimezoneOffset
    },
    {
      seasonElement: toElementEn(seasonElement),
      branch: toChiVi(pillars.month.chi),
      monthOrderFromLichun: core.monthCC?.monthOrderFromLichun || null
    }
  );
  const usefulGodRule = {
    ruleId: core.dungThan?.ruleId || RULE_IDS.USEFUL_GODS_CORE,
    ruleVersion: core.dungThan?.ruleVersion || getRuleMeta(RULE_IDS.USEFUL_GODS_CORE).ruleVersion,
    inputs: core.dungThan?.inputs || {
      dayMasterElement,
      monthBranch: pillars.month.chi,
      bodyStrength: strength
    },
    outputs: core.dungThan?.outputs || {
      roles: {
        dungThan: core.dungThan?.dung_than || [],
        hyThan: core.dungThan?.hy_than || [],
        kyThan: core.dungThan?.ky_than || []
      },
      reasoning: core.dungThan?.reasoning || []
    },
    explain_vi: core.dungThan?.explain_vi || getRuleMeta(RULE_IDS.USEFUL_GODS_CORE).explain_vi
  };

  return {
    engine: {
      name: 'BaZi Core Engine',
      version: '1.0.0',
      ruleVersion: 'bazi-core-v1'
    },
    meta: {
      engine: 'BaziEngine',
      version: '1.0',
      method: 'solar-term + rule-based',
      verified_by_jdn: true,
      timezoneOffsetMinutes: core.normalizedTimezoneOffset,
      rolloverAt23: core.rolloverAt23,
      assumptions: core.assumptions,
      disclaimer: 'Kết quả chỉ mang tính tham khảo/học thuật/giải trí.'
    },
    tu_tru: {
      nam: { ...pillars.year, rule: pillarRules.year },
      thang: { ...pillars.month, rule: pillarRules.month },
      ngay: { ...pillars.day, rule: pillarRules.day },
      gio: { ...pillars.hour, rule: pillarRules.hour }
    },
    pillar_rules: pillarRules,
    month_pillar: {
      can: pillars.month.can,
      chi: pillars.month.chi,
      monthIndex: core.monthCC?.monthIndex ?? null,
      monthBranchIndex: core.monthCC?.monthBranchIndex ?? null,
      monthOrderFromLichun: core.monthCC?.monthOrderFromLichun ?? null,
      derivedFrom: 'solar_term',
      termUsed: core.monthCC?.solarTerm ?? null,
      termTimeLocal: formatLocalDateTime(core.monthCC?.solarTermStart, core.normalizedTimezoneOffset),
      nextTerm: core.monthCC?.nextSolarTerm ?? null,
      nextTermTimeLocal: formatLocalDateTime(core.monthCC?.nextSolarTermStart, core.normalizedTimezoneOffset),
      ruleVersion: 'bazi-core-v1',
      verified: core.monthVerified
    },
    nhat_chu: {
      can: pillars.day.can,
      ngu_hanh: dayMasterElement,
      am_duong: dayMasterPolarity,
      verified_by: 'JDN',
      verified: true,
      jdn: core.dayCC?.jdn ?? null,
      jdnConvention: core.dayCC?.jdnConvention ?? null,
      reference: core.dayCC?.reference ?? null
    },
    day_master: {
      stem: toCanVi(pillars.day.can),
      element: toElementVi(dayMasterElement),
      verified_by: 'JDN',
      verified: true
    },
    analysis: {
      dayMaster: {
        ...dayMasterRule,
        chi: toChiVi(pillars.day.chi),
        strength,
        constitutionKey: strength,
        relativeStrength: null
      },
      season: seasonRule
    },
    favorableElements: {
      roles: {
        dungThan: (core.dungThan?.dung_than || []).map(toElementEn),
        hyThan: (core.dungThan?.hy_than || []).map(toElementEn),
        kyThan: (core.dungThan?.ky_than || []).map(toElementEn)
      },
      labels: {
        vi: {
          dungThan: (core.dungThan?.dung_than || []).map(toElementVi),
          hyThan: (core.dungThan?.hy_than || []).map(toElementVi),
          kyThan: (core.dungThan?.ky_than || []).map(toElementVi)
        }
      },
      reasoning: core.dungThan?.reasoning || [],
      rule: usefulGodRule
    },
    ngu_hanh: buildNguHanhSummary(core.fiveElements, core.fiveElementsMeta),
    thap_than: {
      nam: [core.tenGods.year, pillars.year.can_element],
      thang: [core.tenGods.month, pillars.month.can_element],
      ngay: [core.tenGods.day, pillars.day.can_element],
      gio: [core.tenGods.hour, pillars.hour.can_element]
    },
    quan_he: core.quanHe,
    dai_van: core.daiVan.periods,
    dai_van_meta: core.daiVanMeta,
    dai_van_dataset: core.daiVanDataset,
    luan_doan: {
      suc_khoe: core.readings.health,
      su_nghiep: core.readings.career,
      tai_van: core.readings.wealth
    },
    than_sat: core.stars,
    four_pillars: pillars,
    five_elements: core.fiveElements,
    five_elements_meta: core.fiveElementsMeta,
    dung_than: core.dungThan.dung_than,
    hy_than: core.dungThan.hy_than,
    ky_than: core.dungThan.ky_than,
    dung_than_reasoning: core.dungThan.reasoning || [],
    dung_than_pattern: core.dungThan.pattern || 'Thong Cach',
    yong_shen: {
      element: core.dungThan.dung_than?.[0] || null,
      role: 'Dụng thần',
      reason: core.dungThan.reason || null,
      ruleVersion: core.dungThan.ruleVersion || 'bazi-core-v1',
      verified: Boolean(core.dungThan.verified)
    },
    ten_gods: core.tenGods,
    ten_god_counts: core.tenGodCounts,
    interactions: core.interactions,
    yearCanChi: core.yearCanChi,
    fortune: core.fortune,
    warnings: {
      engine: engineWarnings,
      health: core.readings.health,
      career: core.readings.career,
      wealth: core.readings.wealth
    },
    disclaimer: 'Chỉ mang tính tham khảo, giải trí.'
  };
}

export function buildBaziReportStandard(input = {}) {
  const standardInput = normalizeStandardInput(input);
  const core = calculateBaziCore(standardInput);
  const { pillars, dayMasterElement, dayMasterPolarity, fiveElements } = core;
  const engineWarnings = [];
  if (!core.monthVerified) {
    engineWarnings.push('month_pillar_unverified');
  }
  if (!core.dungThan?.verified) {
    engineWarnings.push('dung_than_unverified');
  }
  const strength = resolveDayMasterStrength({
    dayMasterElement,
    monthBranch: pillars.month.chi
  });
  const usefulGodRule = {
    ruleId: core.dungThan?.ruleId || RULE_IDS.USEFUL_GODS_CORE,
    ruleVersion: core.dungThan?.ruleVersion || getRuleMeta(RULE_IDS.USEFUL_GODS_CORE).ruleVersion,
    explain_vi: core.dungThan?.explain_vi || getRuleMeta(RULE_IDS.USEFUL_GODS_CORE).explain_vi
  };
  const dominantElement = Object.entries(fiveElements || {}).sort(([, a], [, b]) => b - a)[0]?.[0] || null;
  const dominantElementVi = toElementVi(dominantElement);
  const guidance = dominantElementVi ? CAREER_GUIDANCE?.vi?.[dominantElementVi] : null;
  const yearElement = toElementEn(getCanElement(core.yearCanChi.can));
  const personality = getPersonalityProfile(dayMasterElement, 'vi') || {
    coreTraits: [],
    strengths: [],
    risks: [],
    balanceAdvice: []
  };

  const missingElements = Object.entries(fiveElements || {})
    .filter(([, value]) => value === 0)
    .map(([key]) => toElementEn(key))
    .filter(Boolean);

  const wealthRating = core.readings.wealth?.rating ?? 6;
  const financeStyle = wealthRating >= 8 ? 'growth' : wealthRating <= 4 ? 'defensive' : 'stable';
  const financeRisk = wealthRating >= 8 ? 'low' : wealthRating <= 4 ? 'high' : 'medium';
  const annualImpact = core.fortune?.annual?.xung ? 'caution' : 'neutral';

  return {
    engine: {
      name: 'BaZi Core Engine',
      version: '1.0.0',
      ruleVersion: 'bazi-core-v1'
    },
    meta: {
      engine: 'BaziEngine',
      version: '1.0',
      method: 'solar-term + rule-based',
      verified_by_jdn: true,
      timezoneOffsetMinutes: core.normalizedTimezoneOffset,
      rolloverAt23: core.rolloverAt23,
      assumptions: core.assumptions,
      disclaimer: 'Kết quả chỉ mang tính tham khảo/học thuật/giải trí.'
    },
    pillars: {
      year: buildStandardPillar(pillars.year, 'solar_term'),
      month: {
        ...buildStandardPillar(pillars.month, 'solar_term', {
          termUsed: core.monthCC?.solarTerm ?? null,
          termTimeLocal: formatLocalDateTime(core.monthCC?.solarTermStart, core.normalizedTimezoneOffset),
          nextTerm: core.monthCC?.nextSolarTerm ?? null,
          nextTermTimeLocal: formatLocalDateTime(core.monthCC?.nextSolarTermStart, core.normalizedTimezoneOffset),
          monthIndex: core.monthCC?.monthIndex ?? null,
          monthBranchIndex: core.monthCC?.monthBranchIndex ?? null,
          monthOrderFromLichun: core.monthCC?.monthOrderFromLichun ?? null
        }),
        verified: core.monthVerified
      },
      day: {
        ...buildStandardPillar(pillars.day, 'jdn', {
          jdn: core.dayCC?.jdn ?? null,
          jdnConvention: core.dayCC?.jdnConvention ?? null,
          reference: core.dayCC?.reference ?? null,
          trace: pillars.day.meta || null
        }),
        dayMaster: true
      },
      hour: buildStandardPillar(pillars.hour, 'hour_rule')
    },
    dayMaster: {
      can: toCanVi(pillars.day.can),
      element: toElementVi(dayMasterElement),
      strength
    },
    fiveElements: mapElementLevels(fiveElements),
    fiveElementsMeta: core.fiveElementsMeta,
    usefulGods: {
      usefulGod: (core.dungThan.dung_than || []).map(toElementEn),
      favorableGod: (core.dungThan.hy_than || []).map(toElementEn),
      unfavorableGod: (core.dungThan.ky_than || []).map(toElementEn),
      usefulGodType: strength === 'weak' ? 'life_supporting' : 'regulating',
      verified: Boolean(core.dungThan.verified),
      ruleId: usefulGodRule.ruleId,
      ruleVersion: usefulGodRule.ruleVersion,
      explain_vi: usefulGodRule.explain_vi,
      reasoning: core.dungThan.reasoning || []
    },
    personality: {
      coreTraits: personality.coreTraits,
      strengths: personality.strengths,
      risks: personality.risks,
      balanceAdvice: personality.balanceAdvice
    },
    career: {
      dominantElement: toElementEn(dominantElement),
      suitableClusters: guidance?.fit || [],
      avoidClusters: guidance?.avoid || [],
      workStyle: core.readings.career?.khuyenNghi || []
    },
    finance: {
      style: financeStyle,
      riskLevel: financeRisk,
      advice: core.readings.wealth?.khuyenNghi || []
    },
    relationship: {
      expressionStyle: dayMasterPolarity === 'Duong' ? 'bộc lộ' : 'nội tâm',
      risk: core.quanHe?.xung?.length >= 2 ? ['căng thẳng do xung đột'] : [],
      balanceAdvice: core.quanHe?.xung?.length >= 2 ? ['ưu tiên lắng nghe và chia sẻ'] : []
    },
    health: {
      weakElement: missingElements[0] || null,
      lifestyleAdvice: core.readings.health?.khuyenNghi || []
    },
    yearTrends: [
      {
        year: core.yearCanChi.year,
        element: yearElement,
        impact: annualImpact,
        elementBasis: 'heavenly_stem'
      }
    ],
    warnings: engineWarnings
  };
}
